import type { Sample } from './types';

/**
 * Metric values carry an explicit status so the UI never shows a misleading zero.
 * Metrics use the *true* altitude z so that sensor noise does not prevent settling.
 */
export type MetricStatus = 'ok' | 'not-reached' | 'not-settled' | 'not-applicable';

export interface Metric {
  value: number | null;
  status: MetricStatus;
}

const ok = (value: number): Metric => ({ value, status: 'ok' });
const none = (status: Exclude<MetricStatus, 'ok'>): Metric => ({ value: null, status });

export function metricText(m: Metric, unit: string, digits = 2): string {
  switch (m.status) {
    case 'ok':
      return `${m.value!.toFixed(digits)}${unit ? ' ' + unit : ''}`;
    case 'not-reached':
      return 'not reached';
    case 'not-settled':
      return 'not settled in this run';
    case 'not-applicable':
      return 'not applicable';
  }
}

export interface StepWindow {
  /** Time the reference changed (or the event of interest began), s. */
  tStart: number;
  /** End of the evaluation window, s. */
  tEnd: number;
  /** Value before the change. */
  from: number;
  /** Target after the change. */
  to: number;
  /** Absolute settling tolerance, m. */
  tolerance: number;
}

function windowSamples(trace: Sample[], w: { tStart: number; tEnd: number }): Sample[] {
  return trace.filter((s) => s.t >= w.tStart - 1e-9 && s.t <= w.tEnd + 1e-9);
}

/** Rise time between 10 % and 90 % of the reference change. */
export function riseTime(trace: Sample[], w: StepWindow): Metric {
  const delta = w.to - w.from;
  if (Math.abs(delta) < 1e-12) return none('not-applicable');
  const pts = windowSamples(trace, w);
  const frac = (s: Sample) => (s.z - w.from) / delta;
  const crossing = (level: number): number | null => {
    for (let i = 0; i < pts.length; i++) {
      if (frac(pts[i]) >= level) {
        if (i === 0) return pts[0].t;
        const a = pts[i - 1];
        const b = pts[i];
        const fa = frac(a);
        const fb = frac(b);
        return a.t + ((level - fa) / (fb - fa)) * (b.t - a.t);
      }
    }
    return null;
  };
  const t10 = crossing(0.1);
  const t90 = crossing(0.9);
  if (t10 === null || t90 === null) return none('not-reached');
  return ok(t90 - t10);
}

/** Overshoot beyond the target, normalized by the step magnitude (0.1 = 10 %). */
export function overshoot(trace: Sample[], w: StepWindow): Metric {
  const delta = w.to - w.from;
  if (Math.abs(delta) < 1e-12) return none('not-applicable');
  const pts = windowSamples(trace, w);
  if (!pts.length) return none('not-reached');
  const dir = Math.sign(delta);
  let peak = 0;
  for (const s of pts) peak = Math.max(peak, dir * (s.z - w.to));
  return ok(peak / Math.abs(delta));
}

/** Largest excursion from the target within the window, m (useful for zero-amplitude steps). */
export function peakDeviation(trace: Sample[], w: { tStart: number; tEnd: number; to: number }): Metric {
  const pts = windowSamples(trace, w);
  if (!pts.length) return none('not-applicable');
  return ok(Math.max(...pts.map((s) => Math.abs(s.z - w.to))));
}

/**
 * Settling time, measured from tStart: the earliest time after which |z − target| stays within
 * the tolerance for the rest of the window.
 */
export function settlingTime(trace: Sample[], w: StepWindow): Metric {
  const pts = windowSamples(trace, w);
  if (!pts.length) return none('not-settled');
  const inside = (s: Sample) => Math.abs(s.z - w.to) <= w.tolerance;
  if (!inside(pts[pts.length - 1])) return none('not-settled');
  // A settling claim needs some evidence of staying settled; demand the window extend past the entry.
  let i = pts.length - 1;
  while (i > 0 && inside(pts[i - 1])) i--;
  if (i === pts.length - 1 && pts.length > 1) return none('not-settled');
  return ok(Math.max(0, pts[i].t - w.tStart));
}

/** Mean true error r − z over the final window, m. */
export function finalWindowMeanError(trace: Sample[], windowSeconds: number): Metric {
  if (!trace.length) return none('not-applicable');
  const tEnd = trace[trace.length - 1].t;
  const pts = trace.filter((s) => s.t >= tEnd - windowSeconds - 1e-9);
  if (pts.length < 2) return none('not-applicable');
  return ok(pts.reduce((acc, s) => acc + (s.r - s.z), 0) / pts.length);
}

/** Count sign changes of the true error (target crossings) in the window. */
export function crossings(trace: Sample[], w: { tStart: number; tEnd: number }): number {
  const pts = windowSamples(trace, w);
  let count = 0;
  let prev = 0;
  for (const s of pts) {
    const sign = Math.sign(s.r - s.z);
    if (sign !== 0) {
      if (prev !== 0 && sign !== prev) count++;
      prev = sign;
    }
  }
  return count;
}

export interface RunMetrics {
  riseTime: Metric;
  overshoot: Metric;
  settlingTime: Metric;
  finalMeanError: Metric;
  peakDeviation: Metric;
}

/**
 * Default metrics for a run: evaluated on the first reference change if there is one,
 * otherwise on holding the initial reference (zero-amplitude step).
 */
export function summarizeRun(
  trace: Sample[],
  opts: { tolerance?: number; stepTime?: number | null; finalWindow?: number } = {},
): RunMetrics {
  const tolerance = opts.tolerance ?? 0.05;
  if (!trace.length) {
    const na = none('not-applicable');
    return { riseTime: na, overshoot: na, settlingTime: na, finalMeanError: na, peakDeviation: na };
  }
  const tEnd = trace[trace.length - 1].t;
  let tStart = 0;
  let from = trace[0].z;
  let to = trace[0].r;
  const stepIdx = trace.findIndex((s, i) => i > 0 && Math.abs(s.r - trace[i - 1].r) > 1e-12);
  if (opts.stepTime !== undefined && opts.stepTime !== null) {
    tStart = opts.stepTime;
    const before = trace.filter((s) => s.t < tStart);
    from = before.length ? before[before.length - 1].r : trace[0].r;
    to = trace.find((s) => s.t >= tStart - 1e-9)?.r ?? to;
  } else if (stepIdx > 0) {
    tStart = trace[stepIdx].t;
    from = trace[stepIdx - 1].r;
    to = trace[stepIdx].r;
  }
  const w: StepWindow = { tStart, tEnd, from, to, tolerance };
  return {
    riseTime: riseTime(trace, w),
    overshoot: overshoot(trace, w),
    settlingTime: settlingTime(trace, w),
    finalMeanError: finalWindowMeanError(trace, opts.finalWindow ?? 2),
    peakDeviation: peakDeviation(trace, { tStart, tEnd, to }),
  };
}
